import { inject, nextTick, onMounted, onScopeDispose, reactive, toRef, watch } from "vue";
import { toBoolean } from "#ce/utils/props";
import { GOOGLE_MAPS_LOADER, useGoogleMaps } from "./useGoogleMaps";

/**
 * Creates one Google map in `canvas` once Maps loads, unless `defer` holds it back, and disposes it
 * with the owner. Emits `map-ready`, `map-error`, `map-dispose` and `viewport-change` (a drag or zoom).
 * Returns `initialize` and `currentMap` for the element to expose.
 */
export function useGoogleMap(props, emit, canvas) {
  const loader = inject(GOOGLE_MAPS_LOADER, useGoogleMaps);
  const { loadGoogleMaps } = loader(
    reactive({ apiKey: toRef(props, "apiKey"), version: toRef(props, "apiVersion"), loadOnMount: false }),
  );
  let map;
  let maps;
  let pending;
  let disposed = false;

  /** Resolves with the live map, sharing concurrent initialization; rejects load failures for retry. */
  function initialize() {
    if (disposed) {
      return Promise.resolve(undefined);
    }
    if (map) {
      return Promise.resolve(map);
    }
    pending ??= createMap().finally(clearPending);
    return pending;
  }

  function clearPending() {
    pending = undefined;
  }

  async function createMap() {
    try {
      maps = await loadGoogleMaps();
      await nextTick();
      if (disposed) {
        return undefined;
      }
      map = new maps.Map(canvas.value, props.options);
      map.addListener("dragend", reportDrag);
      map.addListener("zoom_changed", reportZoom);
      emit("map-ready", { map, maps });
      return map;
    } catch (error) {
      if (!disposed) {
        emit("map-error", error);
      }
      throw error;
    }
  }

  function reportViewport(source) {
    const bounds = map?.getBounds()?.toJSON();
    if (bounds) {
      emit("viewport-change", { bounds, source });
    }
  }

  function reportDrag() {
    reportViewport("drag");
  }

  function reportZoom() {
    reportViewport("zoom");
  }

  function updateOptions(options) {
    map?.setOptions(options);
  }

  function initializeUnlessDeferred() {
    if (!toBoolean(props.defer)) {
      initialize().catch(ignoreReportedLoadError);
    }
  }

  function ignoreReportedLoadError() {}

  function dispose() {
    disposed = true;
    if (map) {
      emit("map-dispose", map);
      maps.event.clearInstanceListeners(map);
      map.unbindAll();
      map = undefined;
    }
  }

  watch(() => props.options, updateOptions, { deep: true });
  watch(() => props.defer, initializeUnlessDeferred);
  onMounted(initializeUnlessDeferred);
  onScopeDispose(dispose);
  function currentMap() {
    return map;
  }
  return { initialize, currentMap };
}
